import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Divider,
  Grid,
  Card,
  CardContent,
  Autocomplete,
} from '@mui/material';
import {
  MdAdd,
  MdDelete,
  MdPrint,
  MdSearch,
  MdClose,
  MdReceipt,
  MdPayment,
} from 'react-icons/md';
import AppLayout from '../components/AppLayout';
import { getData, postData } from '../utils/serverHelper';
import { POS_BILLING, POS_BILLING_ID } from '../utils/urls';
import { SERVICES, PATIENTS } from '../utils/urls';
import { toast } from 'react-toastify';
import moment from 'moment';
import { useTenant } from '../context/TenantContext';
import { useLabels } from '../utils/labels';

const PosBilling = () => {
  const dispatch = useDispatch();
  const selector = useSelector((state) => state.business);
  const selectorAuth = useSelector((state) => state.auth);
  const { tenant } = useTenant();
  const labels = useLabels();
  
  const [services, setServices] = useState([]);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [cart, setCart] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState('fixed');
  const [taxRate, setTaxRate] = useState(18);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [notes, setNotes] = useState('');
  const [bills, setBills] = useState([]);
  const [selectedBill, setSelectedBill] = useState(null);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const businessId = selectorAuth?.user?.business_id;

  useEffect(() => {
    if (businessId) {
      fetchServices();
      fetchPatients();
      fetchBills();
    }
  }, [businessId]);

  const fetchServices = async () => {
    try {
      const data = await getData(SERVICES);
      setServices(data || []);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchPatients = async () => {
    try {
      const response = await getData(PATIENTS);
      // Handle response structure: could be { success: true, data: [...] } or direct array
      const patientsData = response?.data || response || [];
      setPatients(Array.isArray(patientsData) ? patientsData : []);
    } catch (error) {
      console.error('Error fetching patients:', error);
      setPatients([]);
    }
  };

  const fetchBills = async () => {
    try {
      const data = await getData(POS_BILLING);
      if (data?.success) {
        setBills(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching bills:', error);
    }
  };

  const addToCart = (service) => {
    const existingItem = cart.find(item => item.service_id === service.service_id);
    if (existingItem) {
      setCart(cart.map(item =>
        item.service_id === service.service_id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, {
        service_id: service.service_id,
        item_name: service.service,
        item_type: 'service',
        unit_price: parseFloat(service.charges) || 0,
        quantity: 1,
        discount: 0,
        tax_rate: taxRate
      }]);
    }
  };

  const removeFromCart = (index) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  const updateCartItem = (index, field, value) => {
    const updatedCart = [...cart];
    updatedCart[index] = {
      ...updatedCart[index],
      [field]: field === 'quantity' || field === 'unit_price' || field === 'discount'
        ? parseFloat(value) || 0
        : value
    };
    setCart(updatedCart);
  };

  const calculations = useMemo(() => {
    const subtotal = cart.reduce((sum, item) => {
      const itemTotal = (item.unit_price * item.quantity) - (item.discount || 0);
      return sum + itemTotal;
    }, 0);

    let discountAmount = 0;
    if (discount > 0) {
      if (discountType === 'percent') {
        discountAmount = (subtotal * discount) / 100;
      } else {
        discountAmount = discount;
      }
    }

    const afterDiscount = subtotal - discountAmount;
    const cgst = (afterDiscount * taxRate) / 200;
    const sgst = (afterDiscount * taxRate) / 200;
    const totalAmount = afterDiscount + cgst + sgst;

    return {
      subtotal,
      discountAmount,
      afterDiscount,
      cgst,
      sgst,
      totalAmount
    };
  }, [cart, discount, discountType, taxRate]);

  const handleCreateBill = async () => {
    if (cart.length === 0) {
      toast.error('Please add items to cart');
      return;
    }

    try {
      const billData = {
        business_id: businessId,
        patient_id: selectedPatient?.patient_id || null,
        items: cart,
        discount: discount,
        discount_type: discountType,
        tax_rate: taxRate,
        payment_method: paymentMethod,
        payment_status: 'paid',
        notes: notes
      };

      const response = await postData(POS_BILLING, billData);
      
      if (response?.success) {
        toast.success('Bill created successfully');
        setCart([]);
        setSelectedPatient(null);
        setDiscount(0);
        setNotes('');
        fetchBills();
        setSelectedBill(response.data);
        setPrintDialogOpen(true);
      } else {
        toast.error(response?.error || 'Failed to create bill');
      }
    } catch (error) {
      console.error('Error creating bill:', error);
      toast.error('Failed to create bill');
    }
  };

  const handlePrint = async () => {
    // Fetch full bill details if not already loaded
    if (selectedBill && !selectedBill.items) {
      try {
        const response = await getData(POS_BILLING_ID(selectedBill.bill_id));
        if (response?.success) {
          setSelectedBill(response.data);
        }
      } catch (error) {
        console.error('Error fetching bill details:', error);
      }
    }
    
    // Wait a bit for state update, then print
    setTimeout(() => {
      window.print();
    }, 100);
  };

  const filteredBills = useMemo(() => {
    if (!searchText) return bills;
    const search = searchText.toLowerCase();
    return bills.filter(bill =>
      bill.invoice_number?.toLowerCase().includes(search) ||
      bill.patient_name?.toLowerCase().includes(search) ||
      bill.total_amount?.toString().includes(search)
    );
  }, [bills, searchText]);

  return (
    <AppLayout>
      <Box sx={{ p: 3, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
        <Grid container spacing={3}>
          {/* Left Side - Cart and Billing */}
          <Grid item xs={12} md={7}>
            <Card sx={{ mb: 3, background: 'rgba(255, 255, 255, 0.7)', backdropFilter: 'blur(10px)' }}>
              <CardContent>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                  POS Billing
                </Typography>

                {/* Patient Selection */}
                <Autocomplete
                  options={patients || []}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    return option.full_name ? `${option.full_name}${option.contact ? ' - ' + option.contact : ''}` : 'Unknown';
                  }}
                  value={selectedPatient}
                  onChange={(e, newValue) => setSelectedPatient(newValue)}
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue) return options;
                    const search = inputValue.toLowerCase();
                    return options.filter((option) => {
                      return (
                        option.full_name?.toLowerCase().includes(search) ||
                        option.contact?.includes(search) ||
                        option.email?.toLowerCase().includes(search) ||
                        option.patient_id_number?.toLowerCase().includes(search)
                      );
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`Select ${labels.customer || "Customer"}`}
                      placeholder={`Search ${labels.customer?.toLowerCase() || "customer"} by name, phone, email...`}
                      size="small"
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.patient_id || option.id}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {option.full_name || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.contact || 'No phone'} {option.email ? `• ${option.email}` : ''}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  isOptionEqualToValue={(option, value) => {
                    return (option.patient_id || option.id) === (value?.patient_id || value?.id);
                  }}
                  noOptionsText={`No ${labels.customers?.toLowerCase() || "customers"} found`}
                  sx={{ mb: 2 }}
                />

                {/* Services List */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                    Available Services
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {services.map((service) => (
                      <Chip
                        key={service.service_id}
                        label={`${service.service} - ₹${service.charges}`}
                        onClick={() => addToCart(service)}
                        icon={<MdAdd />}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                </Box>

                {/* Cart */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                    Cart Items
                  </Typography>
                  {cart.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No items in cart
                    </Typography>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Item</TableCell>
                            <TableCell align="right">Price</TableCell>
                            <TableCell align="center">Qty</TableCell>
                            <TableCell align="right">Discount</TableCell>
                            <TableCell align="right">Total</TableCell>
                            <TableCell align="center">Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cart.map((item, index) => (
                            <TableRow key={index}>
                              <TableCell>{item.item_name}</TableCell>
                              <TableCell align="right">₹{item.unit_price.toFixed(2)}</TableCell>
                              <TableCell align="center">
                                <TextField
                                  type="number"
                                  size="small"
                                  value={item.quantity}
                                  onChange={(e) => updateCartItem(index, 'quantity', e.target.value)}
                                  sx={{ width: 60 }}
                                  inputProps={{ min: 1 }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  type="number"
                                  size="small"
                                  value={item.discount || 0}
                                  onChange={(e) => updateCartItem(index, 'discount', e.target.value)}
                                  sx={{ width: 80 }}
                                  inputProps={{ min: 0 }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                ₹{((item.unit_price * item.quantity) - (item.discount || 0)).toFixed(2)}
                              </TableCell>
                              <TableCell align="center">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => removeFromCart(index)}
                                >
                                  <MdDelete />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>

                {/* Discount and Tax */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Discount Type</InputLabel>
                      <Select
                        value={discountType}
                        onChange={(e) => setDiscountType(e.target.value)}
                        label="Discount Type"
                      >
                        <MenuItem value="fixed">Fixed Amount</MenuItem>
                        <MenuItem value="percent">Percentage</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label={discountType === 'percent' ? 'Discount %' : 'Discount Amount'}
                      type="number"
                      value={discount}
                      onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Tax Rate (%)"
                      type="number"
                      value={taxRate}
                      onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)}
                      inputProps={{ min: 0, max: 100 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Payment Method"
                      select
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                    >
                      <MenuItem value="cash">Cash</MenuItem>
                      <MenuItem value="card">Card</MenuItem>
                      <MenuItem value="upi">UPI</MenuItem>
                      <MenuItem value="online">Online</MenuItem>
                      <MenuItem value="cheque">Cheque</MenuItem>
                      <MenuItem value="insurance">Insurance</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Notes (Optional)"
                      multiline
                      rows={2}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </Grid>
                </Grid>

                {/* Summary */}
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    Bill Summary
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">Subtotal:</Typography>
                    <Typography variant="body2">₹{calculations.subtotal.toFixed(2)}</Typography>
                  </Box>
                  {calculations.discountAmount > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">Discount:</Typography>
                      <Typography variant="body2" color="success.main">
                        -₹{calculations.discountAmount.toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">CGST ({taxRate / 2}%):</Typography>
                    <Typography variant="body2">₹{calculations.cgst.toFixed(2)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">SGST ({taxRate / 2}%):</Typography>
                    <Typography variant="body2">₹{calculations.sgst.toFixed(2)}</Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Total Amount:
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      ₹{calculations.totalAmount.toFixed(2)}
                    </Typography>
                  </Box>
                </Paper>

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleCreateBill}
                  disabled={cart.length === 0}
                  sx={{ mt: 2, py: 1.5 }}
                  startIcon={<MdReceipt />}
                >
                  Generate Bill
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Side - Bill History */}
          <Grid item xs={12} md={5}>
            <Card sx={{ background: 'rgba(255, 255, 255, 0.7)', backdropFilter: 'blur(10px)' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Recent Bills
                </Typography>
                
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search bills..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  InputProps={{
                    startAdornment: <MdSearch style={{ marginRight: 8, color: '#666' }} />
                  }}
                  sx={{ mb: 2 }}
                />

                <Box sx={{ maxHeight: '70vh', overflow: 'auto' }}>
                  {filteredBills.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                      No bills found
                    </Typography>
                  ) : (
                    filteredBills.map((bill) => (
                      <Paper
                        key={bill.bill_id}
                        sx={{
                          p: 2,
                          mb: 2,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: '#f5f5f5' }
                        }}
                        onClick={async () => {
                          // Fetch full bill details with items
                          try {
                            const response = await getData(POS_BILLING_ID(bill.bill_id));
                            if (response?.success) {
                              setSelectedBill(response.data);
                            } else {
                              setSelectedBill(bill);
                            }
                          } catch (error) {
                            console.error('Error fetching bill details:', error);
                            setSelectedBill(bill);
                          }
                          setPrintDialogOpen(true);
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {bill.invoice_number}
                          </Typography>
                          <Chip
                            label={bill.payment_status}
                            size="small"
                            color={
                              bill.payment_status === 'paid' ? 'success' :
                              bill.payment_status === 'pending' ? 'warning' : 'default'
                            }
                          />
                        </Box>
                        {bill.patient_name && (
                          <Typography variant="body2" color="text.secondary">
                            Patient: {bill.patient_name}
                          </Typography>
                        )}
                        <Typography variant="body2" color="text.secondary">
                          Date: {moment(bill.bill_date).format('DD MMM YYYY')}
                        </Typography>
                        <Typography variant="h6" sx={{ mt: 1, fontWeight: 600 }}>
                          ₹{parseFloat(bill.total_amount).toFixed(2)}
                        </Typography>
                      </Paper>
                    ))
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Print Dialog */}
        <Dialog
          open={printDialogOpen}
          onClose={() => setPrintDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Invoice</Typography>
              <Box>
                <IconButton onClick={handlePrint} color="primary">
                  <MdPrint />
                </IconButton>
                <IconButton onClick={() => setPrintDialogOpen(false)}>
                  <MdClose />
                </IconButton>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedBill && (
              <Box id="invoice-content" sx={{ p: 3 }}>
                {/* Invoice Header */}
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    {selectedBill.business_name || 'Hospital'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedBill.business_address}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Contact: {selectedBill.business_contact} | Email: {selectedBill.business_email}
                  </Typography>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Invoice Details */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Invoice Number:</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedBill.invoice_number}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Date:</Typography>
                    <Typography variant="body1">
                      {moment(selectedBill.bill_date).format('DD MMM YYYY')}
                    </Typography>
                  </Grid>
                  {selectedBill.patient_name && (
                    <>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Patient:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {selectedBill.patient_name}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Contact:</Typography>
                        <Typography variant="body1">{selectedBill.patient_contact}</Typography>
                      </Grid>
                    </>
                  )}
                </Grid>

                {/* Items Table */}
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Price</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedBill.items?.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.item_name}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">₹{parseFloat(item.unit_price).toFixed(2)}</TableCell>
                          <TableCell align="right">₹{parseFloat(item.total_price).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Totals */}
                <Box sx={{ mt: 3, textAlign: 'right' }}>
                  <Typography variant="body2">
                    Subtotal: ₹{parseFloat(selectedBill.subtotal).toFixed(2)}
                  </Typography>
                  {selectedBill.discount > 0 && (
                    <Typography variant="body2" color="success.main">
                      Discount: -₹{parseFloat(selectedBill.discount).toFixed(2)}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    CGST ({selectedBill.tax_rate / 2}%): ₹{parseFloat(selectedBill.cgst).toFixed(2)}
                  </Typography>
                  <Typography variant="body2">
                    SGST ({selectedBill.tax_rate / 2}%): ₹{parseFloat(selectedBill.sgst).toFixed(2)}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Total: ₹{parseFloat(selectedBill.total_amount).toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Payment Method: {selectedBill.payment_method?.toUpperCase()}
                  </Typography>
                </Box>

                {selectedBill.notes && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Notes: {selectedBill.notes}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ mt: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Thank you for your business!
                  </Typography>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPrintDialogOpen(false)}>Close</Button>
            <Button onClick={handlePrint} variant="contained" startIcon={<MdPrint />}>
              Print
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AppLayout>
  );
};

export default PosBilling;

